import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import { useSnackbar } from "notistack";

import { useResolveExtensionMutation } from "../services/api";
import type { AccountLookupResult } from "../services/api";
import { AccountLookup } from "../components/AccountLookup";

function ExtensionPage() {
  const [account, setAccount] = useState<AccountLookupResult | null>(null);
  const [initialEmail, setInitialEmail] = useState<string | undefined>();
  const [taskToken, setTaskToken] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  const [resolveExtension, { isLoading }] = useResolveExtensionMutation();

  // Read query params on mount and clear them.
  useEffect(() => {
    const query = new URLSearchParams(search);
    const email = query.get("email");
    if (email) setInitialEmail(email);
    const token = query.get("taskToken");
    if (token) setTaskToken(token);
    if (search) navigate(pathname, { replace: true });
  }, [search, pathname, navigate]);

  const handleSubmit = useCallback(async () => {
    try {
      await resolveExtension({
        accountEmail: account!.email,
        taskToken,
      }).unwrap();
      enqueueSnackbar(
        "Provided account email applied to the extension order.",
        { variant: "success" },
      );
    } catch {
      enqueueSnackbar(
        "There was an error communicating with downstream services.",
        { variant: "error" },
      );
    }
  }, [account, taskToken, resolveExtension, enqueueSnackbar]);

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Use this page when an order to extend a license fails to be processed
        automatically. This usually happens when an email address in the
        extension order is different from the email address in the original
        order.
      </Alert>
      <Divider />

      <Typography variant="h5">Step 1: Verify Account</Typography>
      <AccountLookup onAccountChange={setAccount} initialEmail={initialEmail} />

      <Divider />

      <Typography variant="h5">Step 2: Resolve Extension</Typography>
      <TextField
        label="Task Token"
        placeholder="Copy the task token received from the process-order step function here"
        value={taskToken}
        onChange={(e) => setTaskToken(e.target.value)}
        multiline
        rows={4}
        fullWidth
        disabled={!account}
      />
      <Alert severity="warning">
        Clicking &apos;Resolve Extension Order&apos; will reprocess the
        extension order pointed to by the task token, trying to apply it to the
        manually provided account.
      </Alert>
      <Button
        variant="contained"
        color="warning"
        onClick={handleSubmit}
        disabled={isLoading || !account || !taskToken}
      >
        {isLoading ? "Resolving..." : "Resolve Extension Order"}
      </Button>
    </Stack>
  );
}

export { ExtensionPage };
